import { useEffect, useMemo, useState } from 'react';
import {
  useClusterSqlRows,
  isHlc, hlcToMs, hlcDeltaToMs, fmtHlcRelative, fmtHlcDuration,
} from '../data/use-metrics.js';

// Strip the port number off a "host:port" address for display. Returns
// null when the address has no port suffix.
function portOf(addr) {
  if (typeof addr !== 'string') return null;
  const i = addr.lastIndexOf(':');
  if (i < 0) return null;
  const n = Number(addr.slice(i + 1));
  return Number.isFinite(n) ? n : null;
}

function fmtBytes(b) {
  if (b == null || !Number.isFinite(b)) return '—';
  if (b >= 1024 ** 4) return (b / 1024 ** 4).toFixed(1) + ' TB';
  if (b >= 1024 ** 3) return (b / 1024 ** 3).toFixed(1) + ' GB';
  if (b >= 1024 ** 2) return (b / 1024 ** 2).toFixed(1) + ' MB';
  if (b >= 1024)      return (b / 1024).toFixed(1) + ' KB';
  return Math.round(b) + ' B';
}

function kvRowsToObj(rows) {
  if (!rows) return null;
  return Object.fromEntries(rows.map(r => [r[0], r[1]]));
}

// SHOW S3 STATUS shape varies: leader returns named columns; followers
// return {columns:['s3'], data:[['disabled']]}.
function parseS3(columns, rows) {
  if (!rows?.length) return null;
  if (columns?.length === 1 && columns[0] === 's3') {
    return { enabled: false };
  }
  const r = rows[0];
  const idx = name => columns.indexOf(name);
  return {
    enabled: true,
    local:   r[idx('local_txn')],
    synced:  r[idx('synced_txn')],
    lag:     r[idx('txn_lag')],
    pending: r[idx('pending_segments')],
  };
}

function NodeRow({ node, diskRows, s3Cols, s3Rows }) {
  const d = kvRowsToObj(diskRows);
  const s = parseS3(s3Cols, s3Rows);
  const offline = !d;
  const headHash = d?.head_hash;
  const shortHash = typeof headHash === 'string' && headHash.length > 12
    ? `${headHash.slice(0, 8)}…`
    : headHash || '—';
  const port = portOf(node);

  return (
    <tr>
      <td className="fg1" style={{ fontFamily: 'var(--app-mono)', fontSize: 12 }}>{port != null ? `:${port}` : node}</td>
      <td>
        {offline
          ? <span className="pill orange">offline</span>
          : s?.enabled
            ? <span className="pill cyan">leader · S3 on</span>
            : <span className="pill" style={{ background: 'transparent', border: '1px solid var(--app-border)', color: 'var(--app-fg2)' }}>follower</span>}
      </td>
      <td className="num" title={d?.head_txn != null ? String(d.head_txn) : ''}>
        {isHlc(d?.head_txn) ? fmtHlcRelative(d.head_txn) : (d?.head_txn != null ? Number(d.head_txn).toLocaleString() : '—')}
      </td>
      <td style={{ fontFamily: 'var(--app-mono)', fontSize: 11, color: 'var(--app-fg3)' }} title={headHash || ''}>{shortHash}</td>
      <td className="num">{fmtBytes(d?.wal_size_bytes)}</td>
      <td className="num">{d?.segment_count != null ? Number(d.segment_count).toLocaleString() : '—'}</td>
      <td className="num">{fmtBytes(d?.segment_total_bytes)}</td>
      <td className="num" style={{ color: s?.enabled ? (s.lag === 0 ? 'var(--green)' : (isHlc(s.local) ? hlcDeltaToMs(s.lag) > 1000 : s.lag > 1000) ? 'var(--orange)' : 'var(--app-fg1)') : 'var(--app-fg4)' }}>
        {s?.enabled
          ? (isHlc(s.local) ? fmtHlcDuration(s.lag) : (s.lag != null ? s.lag.toLocaleString() + ' txn' : '—'))
          : 'disabled'}
      </td>
      <td className="num">{s?.enabled ? (s.pending ?? 0) : '—'}</td>
    </tr>
  );
}

// Plot S3 lag in milliseconds over time. Single series — local vs synced are
// near-identical at HLC scale (delta is < 0.0001% of the value), so the
// useful signal is the *delta* itself.
function S3LagChart({ history, lagIsHlc }) {
  const w = 1000, h = 220, pad = 24;
  const series = history?.map(d => lagIsHlc ? (hlcDeltaToMs(d.lag) ?? 0) : d.lag) || [];
  if (series.length < 2) {
    return (
      <svg viewBox={`0 0 ${w} ${h}`} className="chart-svg" preserveAspectRatio="none">
        <text x={w / 2} y={h / 2} fill="#555" textAnchor="middle" fontSize="12" fontFamily="ui-monospace,monospace">collecting samples…</text>
      </svg>
    );
  }
  // Floor at 0 (no negative lag); leave headroom above the max so the curve doesn't kiss the top.
  const maxLag = Math.max(...series, 1);
  const dx = (w - pad * 2) / Math.max(1, series.length - 1);
  const yFor = v => pad + (h - pad * 2) * (1 - Math.max(0, v) / maxLag);
  const points = series.map((v, i) => `${pad + i * dx},${yFor(v)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="chart-svg" preserveAspectRatio="none">
      <defs>
        <linearGradient id="lagG" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor="#7B68EE" stopOpacity="0.30" />
          <stop offset="100%" stopColor="#7B68EE" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map(g => (
        <line key={g} x1={pad} x2={w - pad} y1={pad + (h - pad * 2) * g} y2={pad + (h - pad * 2) * g} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
      ))}
      <path d={`M ${pad},${h - pad} L ${points} L ${w - pad},${h - pad} Z`} fill="url(#lagG)" />
      <polyline points={points} fill="none" stroke="#7B68EE" strokeWidth="1.5" />
      <text x={w - pad} y={pad + 10} fill="#888" fontSize="10" fontFamily="ui-monospace,monospace" textAnchor="end">
        peak {lagIsHlc ? `${maxLag.toFixed(0)} ms` : `${maxLag.toFixed(0)} txn`}
      </text>
    </svg>
  );
}

function useS3LagHistory(s3, maxLen = 120) {
  const [history, setHistory] = useState([]);
  const local = s3?.local;
  const synced = s3?.synced;
  useEffect(() => {
    if (local == null || synced == null) return;
    setHistory(h => {
      const next = h.length >= maxLen ? h.slice(h.length - maxLen + 1) : h.slice();
      next.push({ t: Date.now(), local, synced, lag: local - synced });
      return next;
    });
  }, [local, synced, maxLen]);
  return history;
}

export function StoragePage() {
  // Cluster-wide SHOWs: the connected node fans out to peers and prepends
  // a `node` (host:port) column; the hooks group by that column for us.
  // Browser never has to address a non-leader directly.
  const disk = useClusterSqlRows('SHOW DISK STATUS ON ALL NODES', 3000);
  const s3 = useClusterSqlRows('SHOW S3 STATUS ON ALL NODES', 2000);

  // Stable node list = union of nodes seen in either result. Sorted by
  // address so the row order doesn't reshuffle on every poll.
  const nodes = useMemo(() => {
    const set = new Set([
      ...Object.keys(disk.byNode ?? {}),
      ...Object.keys(s3.byNode ?? {}),
    ]);
    return Array.from(set).sort();
  }, [disk.byNode, s3.byNode]);

  // Aggregate KPIs across whichever nodes responded with disk data.
  // Followers still contribute their WAL/segment counters when present.
  const totals = useMemo(() => {
    let walBytes = 0, segCount = 0, segBytes = 0, anyHeadTxn = null;
    let online = 0, offline = 0, s3Only = 0;
    for (const node of nodes) {
      const d = kvRowsToObj(disk.byNode?.[node]);
      if (!d) { offline++; continue; }
      online++;
      const wal = Number(d.wal_size_bytes ?? 0);
      const segs = Number(d.segment_count ?? 0);
      walBytes += wal;
      segCount += segs;
      segBytes += Number(d.segment_total_bytes ?? 0);
      if (d.head_txn != null) anyHeadTxn = Math.max(anyHeadTxn ?? 0, Number(d.head_txn));
      if (wal === 0 && segs === 0) s3Only++;
    }
    return { walBytes, segCount, segBytes, headTxn: anyHeadTxn, online, offline, s3Only };
  }, [nodes, disk.byNode]);

  const nodeStateLabel = (() => {
    const parts = [];
    parts.push(`${totals.online} online`);
    if (totals.offline > 0) parts.push(`${totals.offline} offline`);
    if (totals.s3Only > 0 && totals.s3Only === totals.online) parts.push('all S3-only');
    else if (totals.s3Only > 0) parts.push(`${totals.s3Only} S3-only`);
    return parts.join(' · ');
  })();

  // Pick the leader's S3 status (the one row where enabled === true) for
  // the chart. Inner column shape is the same for every peer because the
  // server uses the local response's columns for the whole result.
  const leaderS3 = useMemo(() => {
    for (const node of nodes) {
      const parsed = parseS3(s3.columns, s3.byNode?.[node]);
      if (parsed?.enabled) return { node, port: portOf(node), ...parsed };
    }
    return null;
  }, [nodes, s3.columns, s3.byNode]);

  const history = useS3LagHistory(leaderS3, 120);
  const lagIsHlc = isHlc(leaderS3?.local);
  // "Behind real-time": ms between now and the most-recently synced txn's
  // wall-clock time. Only meaningful when the txn ID is HLC.
  const behindMs = isHlc(leaderS3?.synced) ? Math.max(0, Date.now() - hlcToMs(leaderS3.synced)) : null;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="crumbs">Admin · Storage & S3</div>
          <h1>Storage & S3</h1>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px 24px' }}>
        {/* KPI row */}
        <div className="kpis" style={{ marginBottom: 16 }}>
          <div className="kpi-card">
            <div className="label">cluster head</div>
            <div className="value" title={totals.headTxn != null ? String(totals.headTxn) : ''}>
              {isHlc(totals.headTxn) ? fmtHlcRelative(totals.headTxn) : (totals.headTxn != null ? totals.headTxn.toLocaleString() : '—')}
            </div>
            <div className="delta">latest txn applied (HLC time)</div>
          </div>
          <div className="kpi-card">
            <div className="label">total WAL on disk</div>
            <div className="value">{fmtBytes(totals.walBytes)}</div>
            <div className="delta" style={{ color: totals.offline > 0 ? 'var(--orange)' : undefined }}>{nodeStateLabel}</div>
          </div>
          <div className="kpi-card">
            <div className="label">total segments on disk</div>
            <div className="value">{fmtBytes(totals.segBytes)}</div>
            <div className="delta">{totals.segCount.toLocaleString()} files{totals.s3Only > 0 ? ` · ${totals.s3Only} S3-only` : ''}</div>
          </div>
          <div className="kpi-card">
            <div className="label">S3 archive lag</div>
            <div className="value" style={{ color: leaderS3 ? (leaderS3.lag === 0 ? 'var(--green)' : (lagIsHlc ? hlcDeltaToMs(leaderS3.lag) > 1000 : leaderS3.lag > 1000) ? 'var(--orange)' : 'var(--cyan)') : 'var(--app-fg3)' }}>
              {leaderS3
                ? (lagIsHlc ? fmtHlcDuration(leaderS3.lag) : leaderS3.lag.toLocaleString())
                : '—'}
            </div>
            <div className="delta">
              {leaderS3
                ? `${leaderS3.pending} pending${behindMs != null ? ` · synced ${behindMs < 1000 ? behindMs + ' ms' : (behindMs / 1000).toFixed(1) + ' s'} ago` : ''}`
                : 'S3 disabled'}
            </div>
          </div>
        </div>

        {/* S3 archive lag chart — single series, lag in ms */}
        {leaderS3 && (
          <div className="k-card full" style={{ marginBottom: 16 }}>
            <div className="k-card-head">
              <span className="title">S3 archive lag · last {Math.round(history.length * 2)}s</span>
              <span style={{ fontSize: 11, color: 'var(--app-fg3)', textTransform: 'none', letterSpacing: 0 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 2, background: '#7B68EE', borderRadius: 1 }} /> {lagIsHlc ? 'lag (ms)' : 'lag (txn)'}
                </span>
              </span>
              <span className="pill" style={{ marginLeft: 'auto', fontFamily: 'var(--app-mono)', fontSize: 11 }}>
                {lagIsHlc ? fmtHlcDuration(leaderS3.lag) : `${leaderS3.lag} txn`}
              </span>
            </div>
            <div style={{ padding: '8px 16px 16px' }}>
              <S3LagChart history={history} lagIsHlc={lagIsHlc} />
            </div>
          </div>
        )}

        {/* Per-node table */}
        <div className="k-card" style={{ marginBottom: 16 }}>
          <div className="k-card-head">
            <span className="title">Per-node disk &amp; S3</span>
          </div>
          <table className="k-table">
            <thead>
              <tr>
                <th>node</th>
                <th>role</th>
                <th className="num">head txn</th>
                <th>head hash</th>
                <th className="num">WAL</th>
                <th className="num">segments</th>
                <th className="num">segment bytes</th>
                <th className="num">S3 lag</th>
                <th className="num">pending</th>
              </tr>
            </thead>
            <tbody>
              {nodes.length === 0
                ? <tr><td colSpan={9} style={{ padding: 16, color: 'var(--app-fg3)' }}>loading…</td></tr>
                : nodes.map(node => (
                    <NodeRow
                      key={node}
                      node={node}
                      diskRows={disk.byNode?.[node]}
                      s3Cols={s3.columns}
                      s3Rows={s3.byNode?.[node]}
                    />
                  ))}
            </tbody>
          </table>
        </div>

        {/* Note about scope */}
        <div style={{ color: 'var(--app-fg3)', fontSize: 12, lineHeight: 1.6, padding: '0 4px' }}>
          KineDB stores WAL and segment files locally and archives them to S3 for disaster recovery — there are no storage tiers.
          When sharding lands across nodes and regions, the S3 archive will also be how new replicas bootstrap.
          Bucket name, endpoint, last-upload time, and upload-throughput counters aren't surfaced server-side yet — coming in a follow-up.
        </div>
      </div>
    </div>
  );
}
